import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { Label } from "@/components/ui/label";
import { useForm } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Package,
  SquareTerminal,
  CircleDashed,
  Check,
  Plus,
  MoreVertical,
  UserRound,
  Trash2,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import type { AuthContextType } from "@/components/AuthContext";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  authAPI,
  billingAPI,
  brandsAPI,
  type Plan,
  invitesAPI,
} from "@/services/api.tsx";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useDataStore } from "@/store/useDataStore";
import { toast } from "sonner";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Brand } from "@/components/AppSidebar";
import { canonicalizeDomain } from "@/utils/domain";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isWorkspaceMemberTier } from "@/lib/memberTier";
import { normalizeUsageMetric } from "@/utils/usage";

type FormValues = {
  companyName: string;
  companywebsite: string;
};

type MemberRow = {
  id: string;
  email: string;
  roleLabel: "Creator" | "Company Member" | "Brand Member";
  roleType: "creator" | "company" | "brand";
  brandIds: string[];
  collaboratorIds: string[];
};

const CompanyPage = ({ user }: { user: AuthContextType["user"] }) => {
  const { t } = useLocale();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      companyName: user?.company?.name || "",
      companywebsite: user?.company?.url || "",
    },
  });
  const navigate = useNavigate();
  const { isTrialEnded, plan, brands, currentBrand, company: activeCompany } =
    useOutletContext<{
    isTrialEnded: boolean;
    plan: Plan;
    brands: Brand[];
    currentBrand?: Brand;
    company?: any;
  }>();
  const activeAccessRole =
    activeCompany?.role || currentBrand?.accessRole;
  const isCollaborator = activeAccessRole
    ? activeAccessRole !== "company_admin"
    : isWorkspaceMemberTier(user?.tier);
  const activeCompanyId =
    activeCompany?.id ||
    activeCompany?.companyId ||
    currentBrand?.companyId ||
    currentBrand?.company?.id ||
    currentBrand?.company?.companyId ||
    null;
  const activeBrands = useMemo(() => {
    if (!activeCompanyId) return brands;
    return brands.filter((brand) => {
      const brandCompanyId =
        brand.companyId || brand.company?.id || brand.company?.companyId;
      return !brandCompanyId || brandCompanyId === activeCompanyId;
    });
  }, [activeCompanyId, brands]);
  const { data: billingSubscription } = useQuery({
    queryKey: ["billing-subscription", user?.id ?? "me"],
    queryFn: billingAPI.getSubscription,
    enabled: !isCollaborator,
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const hasBillingStatus = Boolean(billingSubscription?.subscription_status);
  const fallbackLegacyTrial =
    !hasBillingStatus && user?.tier === "trial" && !isTrialEnded;
  const planLabel = isCollaborator
    ? "Workspace Plan"
    : plan?.name || (fallbackLegacyTrial ? "Trial" : "");
  const cancellationDateSource = billingSubscription?.cancel_at_period_end
    ? billingSubscription?.subscription_status === "trialing"
      ? billingSubscription?.trial_end
      : billingSubscription?.current_period_end
    : null;
  const cancellationLabel = (() => {
    if (!cancellationDateSource) return null;
    const parsed = new Date(cancellationDateSource);
    if (Number.isNaN(parsed.getTime())) return null;
    return `Cancels on ${new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(parsed)}`;
  })();

  useEffect(() => {
    setValue("companyName", activeCompany?.name || user?.company?.name || "");
    setValue("companywebsite", activeCompany?.url || user?.company?.url || "");
  }, [
    activeCompany?.name,
    activeCompany?.url,
    setValue,
    user?.company?.name,
    user?.company?.url,
  ]);

  const queryClient = useQueryClient();
  const { storedLimitPrompts, setStoredLimitPrompts } = useDataStore();
  const resetDataStore = useDataStore((s) => s.reset);

  const { data: limits = storedLimitPrompts } = useQuery({
    queryKey: ["limits", user?.id],
    queryFn: async () => {
      const res = await authAPI.getMeUsage();
      return res;
    },
    staleTime: Infinity,
    enabled: !storedLimitPrompts,
  });
  const promptUsage = normalizeUsageMetric(limits?.prompts);
  const runUsage = normalizeUsageMetric(limits?.runs);

  useEffect(() => {
    setStoredLimitPrompts(limits);
  }, [limits]);

  useEffect(() => {
    if (user?.id) {
      resetDataStore();
    }
  }, [user?.id]);

  const [inviteEmail, setInviteEmail] = useState("");
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [memberRole, setMemberRole] = useState<"company" | "brand">("brand");
  const [memberBrand, setMemberBrand] = useState("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [managedMember, setManagedMember] = useState<MemberRow | null>(null);
  const [managedBrandIds, setManagedBrandIds] = useState<string[]>([]);
  const [managedRoleType, setManagedRoleType] = useState<"company" | "brand">(
    "brand",
  );

  const { data: invitesResponse, isLoading: invitesLoading } = useQuery({
    queryKey: ["invites", activeCompanyId],
    queryFn: () => invitesAPI.listInvites(activeCompanyId),
    enabled: !!user?.id && !isCollaborator,
  });

  const { data: collaboratorsResponse, isLoading: collaboratorsLoading } =
    useQuery({
      queryKey: ["collaborators", activeCompanyId],
      queryFn: () => brandsAPI.getCollaborators(activeCompanyId),
      enabled: !!user?.id,
    });

  const allInvites = invitesResponse?.invites || [];
  const pendingInvites = allInvites.filter(
    (invite) => invite.status === "pending",
  );
  const acceptedCollaborators = (
    collaboratorsResponse?.collaborators || []
  ).filter((collaborator) => collaborator.status === "accepted");

  const members = useMemo<MemberRow[]>(() => {
    const items = new Map<string, MemberRow>();

    const creatorEmail = collaboratorsResponse?.creator?.email;
    if (creatorEmail) {
      items.set(`creator:${creatorEmail.toLowerCase()}`, {
        id: "creator",
        email: creatorEmail,
        roleLabel: "Creator",
        roleType: "creator",
        brandIds: activeBrands
          .map((brand) => brand.brandId || brand.id)
          .filter(Boolean) as string[],
        collaboratorIds: [],
      });
    }

    (collaboratorsResponse?.companyMembers || []).forEach((membership) => {
      const roleLabel = "Company Member";
      const memberBrandIds = activeBrands
        .map((brand) => brand.brandId || brand.id)
        .filter(Boolean) as string[];
      const key = `${membership.email.toLowerCase()}:${roleLabel}`;
      const existing = items.get(key);
      if (existing) {
        existing.brandIds = Array.from(
          new Set([...existing.brandIds, ...memberBrandIds]),
        );
        return;
      }
      items.set(key, {
        id: membership.id,
        email: membership.email,
        roleLabel,
        roleType: "company",
        brandIds: memberBrandIds,
        collaboratorIds: [],
      });
    });

    acceptedCollaborators.forEach((collaborator) => {
      const key = `${collaborator.email.toLowerCase()}:Brand Member`;
      const brandId = collaborator.brand?.id;
      const existing = items.get(key);
      if (existing) {
        if (brandId && !existing.brandIds.includes(brandId)) {
          existing.brandIds.push(brandId);
        }
        if (
          collaborator.id &&
          !existing.collaboratorIds.includes(collaborator.id)
        ) {
          existing.collaboratorIds.push(collaborator.id);
        }
        return;
      }
      items.set(key, {
        id: collaborator.id,
        email: collaborator.email,
        roleLabel: "Brand Member",
        roleType: "brand",
        brandIds: brandId ? [brandId] : [],
        collaboratorIds: collaborator.id ? [collaborator.id] : [],
      });
    });

    return Array.from(items.values()).sort((left, right) => {
      const roleOrder = {
        Creator: 0,
        "Company Member": 1,
        "Brand Member": 2,
      } as const;
      return roleOrder[left.roleLabel] - roleOrder[right.roleLabel];
    });
  }, [
    acceptedCollaborators,
    activeBrands,
    collaboratorsResponse?.companyMembers,
    collaboratorsResponse?.creator,
  ]);

  const getRoleBadgeClassName = (
    roleLabel: "Creator" | "Company Member" | "Brand Member",
  ) => {
    if (roleLabel === "Creator") {
      return "border-[#FDBA74] bg-[#FFF7ED] text-[#C2410C]";
    }
    if (roleLabel === "Company Member") {
      return "border-[#C4B5FD] bg-[#F5F3FF] text-[#6D28D9]";
    }
    return "border-[#93C5FD] bg-[#EFF6FF] text-[#1D4ED8]";
  };

  const getMemberAvatarUrl = (email: string) =>
    `https://api.dicebear.com/9.x/notionists-neutral/svg?seed=${encodeURIComponent(email)}`;

  const inviteMutation = useMutation({
    mutationFn: (payload: {
      email: string;
      roleType: "company" | "brand";
      brandIds?: string[];
      companyId?: string | null;
    }) => invitesAPI.createInvite(payload),
    onSuccess: () => {
      toast.success(t("legacy.invitation.sent.x9by6x"));
      setInviteEmail("");
      setSelectedBrandIds([]);
      setMemberBrand("");
      setMemberRole("brand");
      setInviteDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["invites", activeCompanyId] });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to send invitation";
      toast.error(message);
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (email: string) =>
      brandsAPI.deleteCollaboratorByEmail(email, activeCompanyId),
    onSuccess: () => {
      toast.success(t("legacy.member.removed.1hjfs1j"));
      queryClient.invalidateQueries({
        queryKey: ["collaborators", activeCompanyId],
      });
      queryClient.invalidateQueries({ queryKey: ["invites", activeCompanyId] });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to remove member";
      toast.error(message);
    },
  });

  const manageMemberMutation = useMutation({
    mutationFn: async (payload: {
      email: string;
      roleType: "company" | "brand";
      brandIds?: string[];
      companyId?: string | null;
    }) => brandsAPI.manageCollaborator(payload),
    onSuccess: () => {
      toast.success(t("legacy.member.access.updated.miby90"));
      setManageDialogOpen(false);
      setManagedMember(null);
      setManagedBrandIds([]);
      setManagedRoleType("brand");
      queryClient.invalidateQueries({
        queryKey: ["collaborators", activeCompanyId],
      });
      queryClient.invalidateQueries({ queryKey: ["invites", activeCompanyId] });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to update member access";
      toast.error(message);
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: async (inviteId: string) =>
      invitesAPI.cancelInvite(inviteId, activeCompanyId),
    onSuccess: () => {
      toast.success(t("legacy.invite.canceled.926g5n"));
      queryClient.invalidateQueries({ queryKey: ["invites", activeCompanyId] });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to cancel invite";
      toast.error(message);
    },
  });

  const handleInviteSubmit = (event?: React.FormEvent) => {
    event?.preventDefault?.();
    if (!inviteEmail) {
      toast.error(t("legacy.please.enter.an.email.14h9j0u"));
      return;
    }
    let brandIds = selectedBrandIds;
    if (memberRole === "brand" && brandIds.length === 0 && memberBrand) {
      const brand = activeBrands.find((br) => br.name === memberBrand);
      if (brand?.brandId) {
        brandIds = [brand.brandId];
        setSelectedBrandIds([brand.brandId]);
      }
    }
    if (memberRole === "brand" && !brandIds.length) {
      toast.error(t("legacy.please.select.a.brand.1q1t2nr"));
      return;
    }
    inviteMutation.mutate({
      email: inviteEmail,
      roleType: memberRole,
      companyId: activeCompanyId,
      brandIds: memberRole === "brand" ? brandIds : [],
    });
  };

  const onSubmit = (data: FormValues) => {
    console.log(data);
    toast.success(t("legacy.the.changes.were.saved.ruhync"), {
      icon: (
        <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
          <Check className="w-4 h-4 text-black" />
        </div>
      ),
      position: "bottom-right",
    });
  };

  const handleOpenManage = (member: MemberRow) => {
    if (member.roleType !== "brand") {
      toast.error(t("legacy.only.brand.members.can.be.managed.here.xi0woj"));
      return;
    }
    setManagedMember(member);
    setManagedBrandIds(member.brandIds || []);
    setManagedRoleType("brand");
    setManageDialogOpen(true);
  };

  const handleManageSave = () => {
    if (!managedMember?.email) return;
    if (managedRoleType === "brand" && !managedBrandIds.length) {
      toast.error(t("legacy.select.at.least.one.brand.rwnj3a"));
      return;
    }
    manageMemberMutation.mutate({
      email: managedMember.email,
      roleType: managedRoleType,
      companyId: activeCompanyId,
      brandIds: managedRoleType === "brand" ? managedBrandIds : [],
    });
  };

  const handleRemoveMember = (member: MemberRow) => {
    if (member.roleType === "creator") return;
    removeMemberMutation.mutate(member.email);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-[24px]">
              <Package></Package>
            </div>
            <div>
              <h2 className="flex flex-wrap items-center gap-1 text-lg font-semibold">
                {planLabel}
                {billingSubscription?.cancel_at_period_end &&
                cancellationLabel ? (
                  <span className="ml-2 inline-flex h-[22px] min-w-[128px] items-center justify-center rounded-[8px] border border-[#EF4444] px-[10px] text-xs font-medium leading-4 text-[#EF4444]">
                    {cancellationLabel}
                  </span>
                ) : (
                  <Badge
                    className="border-[#16A34A] text-[#16A34A] ml-2"
                    variant="outline"
                  >
                    <LocalizedText id="legacy.current.plan.kirdj3" />
                  </Badge>
                )}
              </h2>
            </div>
          </div>

          {!isCollaborator && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  navigate(`/dashboard/plans-pricing`);
                }}
                className="w-fit hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
              >
                <LocalizedText id="legacy.upgrade.plan.ztwvc6" />
              </Button>
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-3">
          <div className="flex flex-col gap-3 grow">
            <div className="flex justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-medium">
                <SquareTerminal size={16}></SquareTerminal>{" "}
                <LocalizedText id="legacy.prompts.1lgkknm" />
              </p>
              <p className="text-sm text-muted-foreground">
                {promptUsage.isAvailable
                  ? `${promptUsage.used}/${promptUsage.limit}`
                  : "—/—"}
              </p>
            </div>
            <Progress value={promptUsage.progress} />
          </div>
          <div className="flex flex-col gap-3 grow">
            <div className="flex justify-between">
              <p className="flex items-center gap-2 text-sm font-medium">
                <CircleDashed size={16} />{" "}
                <LocalizedText id="legacy.runs.yjy8hr" />
              </p>
              <p className="text-sm text-muted-foreground">
                {runUsage.isAvailable
                  ? `${runUsage.used}/${runUsage.limit}`
                  : "—/—"}
              </p>
            </div>
            <Progress value={runUsage.progress} />
          </div>
          {/* <div className="flex flex-col gap-3 grow">
            <div className="flex justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-medium">
                <Zap size={16}></Zap> Actions credits
              </p>
              <p className="text-sm text-muted-foreground">Not connected</p>
            </div>
            <Progress value={0} />
          </div> */}
        </div>
      </div>
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <h1 className="text-base text-[#0A0A0A] font-semibold">
              <LocalizedText id="legacy.company.info.eb4e98" />
            </h1>
            <div className="text-muted-foreground text-sm">
              <LocalizedText id="legacy.edit.your.company.data.aaza7" />
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <div>
              <Label
                className="mb-2 text-sm font-medium text-foreground"
                htmlFor="companyName"
              >
                <LocalizedText id="legacy.company.name.eago79" />
              </Label>
              <Input
                disabled={true}
                id="companyName"
                type="text"
                autoComplete="off"
                {...register("companyName")}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
              />
              {errors.companyName && (
                <p className="text-red-500 text-sm mt-2">
                  {(errors.companyName as FieldError)?.message}
                </p>
              )}
            </div>

            <div>
              <Label
                className="mb-2 text-sm font-medium text-foreground"
                htmlFor="companywebsite"
              >
                <LocalizedText id="legacy.website.10ekgqm" />
              </Label>
              <Input
                disabled={true}
                id="companywebsite"
                type="text"
                autoComplete="off"
                {...register("companywebsite")}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
              />
              {errors.companywebsite && (
                <p className="text-red-500 text-sm mt-2">
                  {(errors.companywebsite as FieldError)?.message}
                </p>
              )}
            </div>
          </div>

          {!isCollaborator && (
            <div className="w-full flex justify-center">
              <Button
                type="submit"
                className="w-fit hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
              >
                <LocalizedText id="legacy.save.changes.p1tf81" />
              </Button>
            </div>
          )}
        </form>
        <Separator className="my-5" />
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-base text-[#0A0A0A] font-semibold">
                <LocalizedText id="legacy.members.and.roles.1nv7mxz" />
              </h1>
              <div className="text-muted-foreground text-sm">
                <LocalizedText id="legacy.invite.teammates.and.manage.their.access.1n4dltf" />
              </div>
            </div>

            {!isCollaborator && (
              <Dialog
                open={inviteDialogOpen}
                onOpenChange={setInviteDialogOpen}
              >
                <form onSubmit={handleInviteSubmit}>
                  <DialogTrigger asChild>
                    <Button className="cursor-pointer" variant="outline">
                      <Plus></Plus>{" "}
                      <LocalizedText id="legacy.invite.users.1qtkf2m" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>
                        <LocalizedText id="legacy.invite.users.1qtkf2m" />
                      </DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4">
                      <div className="grid gap-3">
                        <Label htmlFor="invite-email">
                          <LocalizedText id="legacy.email.3brsq1" />
                        </Label>
                        <Input
                          id="invite-email"
                          name="invite-email"
                          placeholder={t("legacy.enter.email.address.lkpwqf")}
                          value={inviteEmail}
                          onChange={(event) =>
                            setInviteEmail(event.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="grid gap-3">
                        <Label htmlFor="role-type">
                          <LocalizedText id="legacy.role.type.1hvkkq1" />
                        </Label>
                        <Select
                          value={memberRole}
                          onValueChange={(value) =>
                            setMemberRole(value as "company" | "brand")
                          }
                        >
                          <SelectTrigger
                            className="px-1 w-full py-1 text-[#0A0A0A] flex **:data-[slot=select-value]:block **:data-[slot=select-value]:truncate @[767px]/card:hidden"
                            size="sm"
                            aria-label={t("legacy.select.a.value.nv8d47")}
                          >
                            <SelectValue
                              placeholder={t("legacy.select.a.role.z5x9aw")}
                            />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl">
                            <SelectItem value="company" className="rounded-lg">
                              <LocalizedText id="legacy.company.member.t8uzci" />
                            </SelectItem>
                            <SelectItem value="brand" className="rounded-lg">
                              <LocalizedText id="legacy.brand.member.mfebny" />
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {memberRole === "brand" && (
                        <div className="grid gap-3">
                          <Label htmlFor="brand-select">
                            <LocalizedText id="legacy.brand.3i2cu6" />
                          </Label>
                          <Select
                            value={memberBrand}
                            onValueChange={(value) => {
                              setMemberBrand(value);
                              const brand = activeBrands.find(
                                (br) => br.name === value,
                              );
                              if (
                                brand?.brandId &&
                                !selectedBrandIds.includes(brand.brandId)
                              ) {
                                setSelectedBrandIds([brand.brandId]);
                              }
                            }}
                          >
                            <SelectTrigger
                              className="px-1 w-full py-1 text-[#0A0A0A] flex **:data-[slot=select-value]:block **:data-[slot=select-value]:truncate @[767px]/card:hidden"
                              size="sm"
                              aria-label={t("legacy.select.a.value.nv8d47")}
                            >
                              <SelectValue
                                className="flex"
                                placeholder={t("legacy.select.a.brand.nmzxx3")}
                              >
                                <div className="flex gap-2">
                                  <img
                                    className="h-5 w-5 shrink-0 rounded-md border object-contain"
                                    src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(activeBrands.find((br) => br.name === memberBrand)?.url) || ""}&size=64`}
                                    alt={`${memberBrand || "Selected brand"} logo`}
                                  />
                                  {memberBrand}
                                </div>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                              {activeBrands.map((brand) => {
                                return (
                                  <SelectItem
                                    key={brand.brandId}
                                    value={brand.name}
                                    className="rounded-lg"
                                  >
                                    <img
                                      className="h-5 w-5 shrink-0 rounded-md border object-contain"
                                      src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(brand?.url) || ""}&size=64`}
                                      alt={`${brand.name} logo`}
                                    />
                                    {brand.name}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button
                          className="cursor-pointer"
                          variant="outline"
                          onClick={() => {
                            setMemberBrand("");
                            setMemberRole("brand");
                          }}
                        >
                          <LocalizedText id="legacy.cancel.18x3nqb" />
                        </Button>
                      </DialogClose>
                      <Button
                        type="submit"
                        className="cursor-pointer hover:bg-[#ff8138] hover:text-white text-white bg-[#FF5E00]"
                        disabled={
                          inviteMutation.isPending ||
                          !inviteEmail ||
                          (memberRole === "brand" && !selectedBrandIds.length)
                        }
                        onClick={() => handleInviteSubmit()}
                      >
                        <LocalizedText id="legacy.send.invite.1xi51ww" />
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </form>
              </Dialog>
            )}
            {!isCollaborator && (
              <Dialog
                open={manageDialogOpen}
                onOpenChange={(next) => {
                  setManageDialogOpen(next);
                  if (!next) {
                    setManagedMember(null);
                    setManagedBrandIds([]);
                    setManagedRoleType("brand");
                  }
                }}
              >
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>
                      <LocalizedText id="legacy.manage.member.access.jec86o" />
                    </DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-3">
                    <div className="text-sm text-muted-foreground">
                      {managedMember?.email || ""}
                    </div>
                    <div className="grid gap-3">
                      <Label htmlFor="manage-role-type">
                        <LocalizedText id="legacy.role.type.1hvkkq1" />
                      </Label>
                      <Select
                        value={managedRoleType}
                        onValueChange={(value) =>
                          setManagedRoleType(value as "company" | "brand")
                        }
                      >
                        <SelectTrigger
                          id="manage-role-type"
                          className="px-1 w-full py-1 text-[#0A0A0A]"
                          size="sm"
                          aria-label={t("legacy.select.a.role.z5x9aw")}
                        >
                          <SelectValue
                            placeholder={t("legacy.select.a.role.z5x9aw")}
                          />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value="brand" className="rounded-lg">
                            <LocalizedText id="legacy.brand.member.mfebny" />
                          </SelectItem>
                          <SelectItem value="company" className="rounded-lg">
                            <LocalizedText id="legacy.company.member.t8uzci" />
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {managedRoleType === "brand" && (
                      <div className="grid gap-2">
                        {activeBrands.map((brand) => {
                          const brandId = brand.brandId || brand.id;
                          const checked = managedBrandIds.includes(brandId);
                          return (
                            <label
                              key={brandId}
                              className="flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(value) => {
                                  if (value) {
                                    setManagedBrandIds((prev) =>
                                      prev.includes(brandId)
                                        ? prev
                                        : [...prev, brandId],
                                    );
                                  } else {
                                    setManagedBrandIds((prev) =>
                                      prev.filter((id) => id !== brandId),
                                    );
                                  }
                                }}
                              />
                              <span className="text-sm">{brand.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {managedRoleType === "company" && (
                      <p className="text-xs text-muted-foreground">
                        <LocalizedText id="legacy.switching.to.company.member.grants.access.to.all.2ujqpl" />
                      </p>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setManageDialogOpen(false)}
                    >
                      <LocalizedText id="legacy.cancel.18x3nqb" />
                    </Button>
                    <Button
                      className="hover:bg-[#ff8138] hover:text-white text-white bg-[#FF5E00]"
                      onClick={handleManageSave}
                      disabled={manageMemberMutation.isPending}
                    >
                      <LocalizedText id="legacy.save.yjzjb8" />
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className={"space-y-4"}>
            <div className="space-y-2">
              {invitesLoading || collaboratorsLoading ? (
                <p className="text-sm text-muted-foreground">
                  <LocalizedText id="legacy.loading.1ooa6x9" />
                </p>
              ) : members.length ? (
                <div className="space-y-2 max-h-64 overflow-auto">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex h-14 w-full items-center justify-between gap-4 rounded-[8px] border border-[#E5E5E5] bg-white px-3"
                    >
                      <div className="flex min-w-0 items-center gap-4">
                        <img
                          src={getMemberAvatarUrl(member.email)}
                          alt={member.email}
                          className="h-8 w-8 shrink-0 rounded-full border border-[#E5E7EB] bg-[#F9FAFB]"
                        />
                        <div className="min-w-0 flex items-center gap-2 overflow-hidden text-sm leading-5">
                          <span className="max-w-[320px] truncate font-normal text-[#111827] no-underline">
                            {member.email}
                          </span>
                          <Badge
                            variant="outline"
                            className={`h-7 rounded-[8px] px-3 text-[12px] font-medium ${getRoleBadgeClassName(member.roleLabel)}`}
                          >
                            {member.roleLabel}
                          </Badge>
                          {member.brandIds.length ? (
                            <span className="flex items-center gap-1 whitespace-nowrap text-xs text-[#6B7280]">
                              <span>in</span>
                              <span className="flex items-center gap-1">
                                {member.brandIds.slice(0, 3).map((brandId) => {
                                  const brand = activeBrands.find(
                                    (item) =>
                                      (item.brandId || item.id) === brandId,
                                  );
                                  if (!brand) return null;
                                  return (
                                    <img
                                      key={brandId}
                                      className="h-6 w-6 shrink-0 rounded-[6px] border object-contain"
                                      src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(brand?.url) || ""}&size=64`}
                                      alt={brand.name}
                                    />
                                  );
                                })}
                              </span>
                              {member.brandIds.length > 3 ? (
                                <span className="text-xs text-[#6B7280]">
                                  +{member.brandIds.length - 3}
                                </span>
                              ) : null}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {!isCollaborator && member.roleType !== "creator" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Manage ${member.email}`}
                              className="h-9 w-9 shrink-0 rounded-[10px] border border-[#E5E7EB] bg-white text-[#6B7280] shadow-[0_1px_2px_rgba(16,24,40,0.06)] hover:bg-[#F9FAFB]"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="min-w-[132px] rounded-[10px] border border-[#E5E7EB] bg-white p-0 shadow-[0_10px_24px_rgba(16,24,40,0.12)]"
                          >
                            {member.roleType === "brand" && (
                              <>
                                <DropdownMenuItem
                                  className="h-11 cursor-pointer px-4 text-sm text-[#111827]"
                                  onClick={() => handleOpenManage(member)}
                                >
                                  <UserRound className="h-4 w-4 text-[#111827]" />
                                  <LocalizedText id="legacy.manage.17pbntw" />
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="m-0" />
                              </>
                            )}
                            <DropdownMenuItem
                              className="h-11 cursor-pointer px-4 text-sm text-[#EF4444] focus:text-[#EF4444]"
                              onClick={() => handleRemoveMember(member)}
                              disabled={removeMemberMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-[#EF4444]" />
                              <LocalizedText id="legacy.remove.1ih9ohv" />
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  <LocalizedText id="legacy.no.members.yet.o66w4h" />
                </p>
              )}
            </div>

            {!isCollaborator && (
              <div className="space-y-3 rounded-lg border border-[#E5E7EB] bg-white p-4">
                <h2 className="text-sm font-semibold text-[#111827]">
                  <LocalizedText id="legacy.pending.members.s6vkxl" />
                </h2>
                {invitesLoading ? (
                  <p className="text-sm text-muted-foreground">
                    <LocalizedText id="legacy.loading.1ooa6x9" />
                  </p>
                ) : pendingInvites.length ? (
                  <div className="space-y-2 max-h-64 overflow-auto">
                    {pendingInvites.map((invite) => (
                      <div
                        key={invite.id}
                        className="flex items-center justify-between gap-3 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2"
                      >
                        <div className="flex flex-col gap-1">
                          <span className="text-[15px] leading-5 font-medium text-[#111827]">
                            {invite.email}
                          </span>
                          <span className="text-xs text-[#6B7280]">
                            {invite.roleType === "company"
                              ? "Company access"
                              : invite.brandNames?.length
                                ? `Brands: ${invite.brandNames.join(", ")}`
                                : "Brand access"}
                          </span>
                          <span className="text-xs text-[#6B7280]">
                            <LocalizedText id="legacy.expires.1xwhzy7" />{" "}
                            {new Date(invite.expiresAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className="border-[#FCD34D] bg-[#FFFBEB] text-[#92400E]"
                          >
                            pending
                          </Badge>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              cancelInviteMutation.mutate(invite.id)
                            }
                            disabled={cancelInviteMutation.isPending}
                          >
                            <LocalizedText id="legacy.cancel.18x3nqb" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    <LocalizedText id="legacy.no.pending.invites.yet.sdaxgn" />
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyPage;
